import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Loader from 'components/atoms/loader';
import colors from 'styles/colors';
import {
  CompradorAgregado,
  chaveDoOrgao,
  fmtBRL,
  fmtDataBR,
  fmtInt,
  linkLicitacao,
  mediana,
  OrgaoPublico,
  priceIntelligenceApi,
  SearchHit,
} from 'services/price_intelligence';
import { Body } from 'components/molecules/price_intelligence_bidding_modal/styles';

interface PriceIntelligenceOrgaoModalProps {
  orgao: CompradorAgregado | null;
  hits: SearchHit[];
  valorDe: (hit: SearchHit) => number | null;
  sufixoUnidade: string;
  onClose: () => void;
}

interface CampoProps {
  col: string;
  label: string;
  valor: string | number | null | undefined;
}

const Campo = ({ col, label, valor }: CampoProps) => (
  <div className={col}>
    <span className="campo-label">{label}</span>
    <p className="campo-valor">{valor || '—'}</p>
  </div>
);

export const amostrasDoOrgao = (hits: SearchHit[], orgao: CompradorAgregado): SearchHit[] => {
  const chave = orgao.orgaoId ? `id:${orgao.orgaoId}` : `nome:${orgao.nome}`;
  return hits.filter((h) => chaveDoOrgao(h.payload) === chave);
};

const PriceIntelligenceOrgaoModal = ({
  orgao,
  hits,
  valorDe,
  sufixoUnidade,
  onClose,
}: PriceIntelligenceOrgaoModalProps) => {
  const [dados, setDados] = useState<OrgaoPublico | null>(null);
  const [carregando, setCarregando] = useState<boolean>(false);
  const orgaoId = orgao ? orgao.orgaoId : null;

  useEffect(() => {
    setDados(null);
    if (!orgaoId) return undefined;
    let ativo = true;
    setCarregando(true);
    priceIntelligenceApi
      .getPublicBody(orgaoId)
      .then((d) => ativo && setDados(d))
      .catch(() => ativo && setDados(null))
      .finally(() => ativo && setCarregando(false));
    return () => {
      ativo = false;
    };
  }, [orgaoId]);

  const amostras = orgao ? amostrasDoOrgao(hits, orgao) : [];
  const valores = amostras.map(valorDe).filter((v): v is number => typeof v === 'number' && v > 0);
  const medianaOrgao = mediana(valores);
  const menor = valores.length ? Math.min.apply(null, valores) : null;
  const local = orgao
    ? [dados && dados.cidade ? dados.cidade : orgao.municipio, (dados && dados.uf) || orgao.uf]
        .filter(Boolean)
        .join('/')
    : '';

  return (
    <Modal size="lg" show={Boolean(orgao)} onHide={onClose} centered>
      <div className="modal-header" style={{ backgroundColor: colors.$dark_light }}>
        <h5 className="modal-title text-white font-weight-bold">Órgão público</h5>
        <button aria-label="Fechar" className="close text-white" type="button" onClick={onClose}>
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <Body className="modal-body">
        {orgao && (
          <>
            <p className="amostra-desc">{(dados && dados.nome) || orgao.nome}</p>
            {carregando && <Loader />}
            {!carregando && (
              <div className="row">
                <Campo col="col-md-3" label="UASG" valor={dados && dados.uasg} />
                <Campo col="col-md-5" label="Endereço" valor={dados && dados.endereco} />
                <Campo col="col-md-4" label="Cidade" valor={local} />
                {dados && dados.site && <Campo col="col-12" label="Site" valor={dados.site} />}
              </div>
            )}
            <div className="amostra-cards">
              <div>
                <span>Aquisições nesta pesquisa</span>
                <strong>{fmtInt(amostras.length)}</strong>
              </div>
              <div>
                <span>Mediana paga</span>
                <strong>
                  {medianaOrgao !== null ? `${fmtBRL(medianaOrgao)}${sufixoUnidade}` : '—'}
                </strong>
              </div>
              <div>
                <span>Menor valor pago</span>
                <strong>{menor !== null ? `${fmtBRL(menor)}${sufixoUnidade}` : '—'}</strong>
              </div>
            </div>
            <div className="table-responsive resultados">
              <table className="table table-hover table-bordered mb-0">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Vencedor</th>
                    <th>Valor</th>
                    <th>Data</th>
                    <th aria-label="Licitação" />
                  </tr>
                </thead>
                <tbody>
                  {amostras.map((h) => {
                    const valor = valorDe(h);
                    const link = linkLicitacao(h.payload.licitacao_id);
                    return (
                      <tr key={String(h.id)}>
                        <td>{h.payload.item_descricao || '—'}</td>
                        <td>{h.payload.fornecedor || '—'}</td>
                        <td className="text-right text-nowrap">
                          {valor !== null ? `${fmtBRL(valor)}${sufixoUnidade}` : '—'}
                        </td>
                        <td className="text-nowrap">{fmtDataBR(h.payload.created)}</td>
                        <td>
                          {link && (
                            <a
                              className="btn btn-sm btn-primary text-nowrap"
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Acessar
                            </a>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Body>
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Fechar
        </button>
      </div>
    </Modal>
  );
};

export default PriceIntelligenceOrgaoModal;
